import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Loader2, Bell, Check, Eye, TrendingUp } from 'lucide-react';
import { reminderPrefs } from '@/lib/api';

export default function ReminderPrefsCard() {
  const [prefs, setPrefs] = React.useState(null);
  const [analytics, setAnalytics] = React.useState(null);
  const [roi, setRoi] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [previewOpen, setPreviewOpen] = React.useState(false);

  React.useEffect(() => {
    reminderPrefs.get().then(setPrefs);
    reminderPrefs.analytics().then(setAnalytics);
    reminderPrefs.roi().then(setRoi).catch(() => {});
  }, []);

  const toggleMs = (d) => {
    setPrefs((p) => {
      const cur = new Set(p.milestones || []);
      cur.has(d) ? cur.delete(d) : cur.add(d);
      return { ...p, milestones: [...cur].sort((a, b) => a - b) };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await reminderPrefs.save({
        milestones: prefs.milestones,
        tone_override: prefs.tone_override || null,
      });
      setPrefs(r);
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } finally { setSaving(false); }
  };

  if (!prefs) return null;

  return (
    <Card className="border-slate-200" data-testid="reminder-prefs-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-[#2ECC9A]" />
          <div className="text-sm font-semibold text-[#1A3A5C]">Applicant payment reminders</div>
          {saved && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]"><Check className="mr-1 h-3 w-3"/> Saved</Badge>}
        </div>
        <p className="mt-2 text-xs text-slate-500">
          Choose which overdue milestones fire a branded reminder email to your applicants.
          Default tone graduates gentle → firm → urgent; override to force one tone for all milestones.
        </p>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <Label className="text-xs">Milestones</Label>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {[7, 14, 21].map((d) => {
                const on = (prefs.milestones || []).includes(d);
                return (
                  <button key={d} data-testid={`milestone-${d}`} onClick={() => toggleMs(d)}
                          className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                            on ? 'border-[#2ECC9A] bg-[#2ECC9A]/10 text-[#0F766E]'
                                : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                          }`}>
                    {on ? '✓ ' : ''}{d} days
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="text-xs">Tone override</Label>
            <Select value={prefs.tone_override || 'auto'}
                    onValueChange={(v) => setPrefs((p) => ({ ...p, tone_override: v === 'auto' ? null : v }))}>
              <SelectTrigger data-testid="tone-override" className="mt-1 h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto (graduated)</SelectItem>
                <SelectItem value="friendly">Always friendly</SelectItem>
                <SelectItem value="firm">Always firm</SelectItem>
                <SelectItem value="urgent">Always urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <Button data-testid="reminder-prefs-save" onClick={save} disabled={saving}
                  className="h-9 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs">
            {saving ? <Loader2 className="h-3 w-3 animate-spin"/> : 'Save preferences'}
          </Button>
          <Button data-testid="reminder-prefs-preview" onClick={() => setPreviewOpen(true)} variant="outline"
                  className="h-9 gap-1 border-slate-200 text-xs">
            <Eye className="h-3 w-3" /> Preview emails
          </Button>
        </div>

        {analytics && (
          <div className="mt-6 border-t border-slate-100 pt-4">
            <div className="text-xs font-semibold text-[#1A3A5C]">Nudge analytics</div>
            <div className="mt-3 grid grid-cols-4 gap-3">
              {[
                { k: 'total_sent', label: 'Total sent' },
                { k: 'recovered_count', label: 'Paid after nudge' },
                { k: 'recovered_amount_aud', label: 'A$ recovered', money: true },
                { k: 'unique_invoices_reminded', label: 'Unique invoices' },
              ].map(({ k, label, money }) => (
                <div key={k} data-testid={`nudge-kpi-${k}`}
                     className="rounded-md border border-slate-200 bg-slate-50 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
                  <div className="mt-1 text-2xl font-bold text-[#1A3A5C]">
                    {money ? `A$${Number(analytics[k] || 0).toLocaleString()}` : analytics[k] || 0}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2 text-[11px] text-slate-500">
              {[7, 14, 21].map((d) => (
                <span key={d} className="rounded-full border border-slate-200 bg-white px-2 py-0.5">
                  {d}d: <b>{analytics.by_milestone?.[d] ?? 0}</b>
                </span>
              ))}
            </div>
          </div>
        )}

        {roi && <RoiSparkline weeks={roi.weeks} />}
      </CardContent>

      <PreviewDialog open={previewOpen} onOpenChange={setPreviewOpen}
                     activeTone={prefs.tone_override}
                     activeMilestones={prefs.milestones || [7, 14, 21]} />
    </Card>
  );
}


function RoiSparkline({ weeks }) {
  const [drilldown, setDrilldown] = React.useState(null); // {week, data}
  if (!weeks || weeks.length === 0) return null;
  const totalRecovered = weeks.reduce((a, w) => a + (w.recovered_aud || 0), 0);
  const totalSent = weeks.reduce((a, w) => a + (w.sent || 0), 0);
  const maxRecovered = Math.max(1, ...weeks.map((w) => w.recovered_aud || 0));
  const maxSent = Math.max(1, ...weeks.map((w) => w.sent || 0));
  const W = 640; const H = 96; const PAD = 8;
  const bw = (W - PAD * 2) / weeks.length;

  const openWeek = async (w) => {
    if (!w.recovered_aud) return; // empty bars don't drill
    setDrilldown({ week: w, data: null });
    try {
      const r = await reminderPrefs.roiWeek(w.start);
      setDrilldown({ week: w, data: r });
    } catch {
      setDrilldown({ week: w, data: { invoices: [], count: 0, total_recovered_aud: 0 } });
    }
  };

  return (
    <div className="mt-6 border-t border-slate-100 pt-4" data-testid="reminder-roi-sparkline">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-semibold text-[#1A3A5C]">
          <TrendingUp className="h-3.5 w-3.5 text-[#2ECC9A]" /> 12-week ROI
        </div>
        <div className="text-[11px] text-slate-500">
          <b>A${totalRecovered.toLocaleString()}</b> recovered · <b>{totalSent}</b> nudges sent
        </div>
      </div>
      <div className="mt-3 overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H + 24}`} className="w-full min-w-[560px]" role="img" aria-label="12-week ROI sparkline">
          {weeks.map((w, i) => {
            const h = ((w.recovered_aud || 0) / maxRecovered) * (H - 20);
            const x = PAD + i * bw + 2;
            const y = H - h;
            const clickable = (w.recovered_aud || 0) > 0;
            return (
              <g key={i}>
                <rect x={x} y={y} width={Math.max(2, bw - 4)} height={h}
                      rx="1.5"
                      fill="#2ECC9A" opacity={clickable ? 0.9 : 0.5}
                      style={{ cursor: clickable ? 'pointer' : 'default' }}
                      onClick={() => openWeek(w)}
                      data-testid={`roi-week-${i}`}>
                  <title>{`Week of ${w.label}: A$${(w.recovered_aud||0).toLocaleString()} recovered · ${w.sent} nudges${clickable ? ' — click for detail' : ''}`}</title>
                </rect>
              </g>
            );
          })}
          <polyline
            fill="none"
            stroke="#1A3A5C"
            strokeWidth="1.5"
            points={weeks.map((w, i) => {
              const x = PAD + i * bw + bw / 2;
              const y = H - ((w.sent || 0) / maxSent) * (H - 20);
              return `${x},${y}`;
            }).join(' ')}
          />
          {weeks.map((w, i) => {
            const x = PAD + i * bw + bw / 2;
            const y = H - ((w.sent || 0) / maxSent) * (H - 20);
            return <circle key={i} cx={x} cy={y} r="2.5" fill="#1A3A5C" />;
          })}
          {weeks.map((w, i) => (
            i % 2 === 0 ? (
              <text key={i} x={PAD + i * bw + bw / 2} y={H + 14}
                    textAnchor="middle" fontSize="9" fill="#64748b">{w.label}</text>
            ) : null
          ))}
        </svg>
      </div>
      <div className="mt-2 flex gap-4 text-[10px] text-slate-500">
        <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-[#2ECC9A]" /> A$ recovered <span className="text-slate-400">· click a bar to see invoices</span></span>
        <span className="flex items-center gap-1"><span className="inline-block h-0.5 w-3 bg-[#1A3A5C]" /> Nudges sent</span>
      </div>

      <RoiDrilldownDialog drilldown={drilldown} onClose={() => setDrilldown(null)} />
    </div>
  );
}


function RoiDrilldownDialog({ drilldown, onClose }) {
  if (!drilldown) return null;
  const { week, data } = drilldown;
  const loading = data === null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="roi-drilldown-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-[#2ECC9A]" />
            Recovered in week of {week.label}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" /></div>
        ) : (
          <div>
            <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-600">
              <b>{data.count}</b> invoice{data.count === 1 ? '' : 's'} · A$<b>{Number(data.total_recovered_aud || 0).toLocaleString()}</b> recovered.
              Each row is credited to this week because the LAST reminder before payment was sent between {new Date(data.week_start).toLocaleDateString('en-AU')} and {new Date(data.week_end).toLocaleDateString('en-AU')}.
            </div>
            {data.invoices.length === 0 ? (
              <div className="mt-3 rounded-md border border-dashed border-slate-200 p-4 text-center text-xs text-slate-500">
                No invoices recovered this week.
              </div>
            ) : (
              <div className="mt-3 max-h-96 overflow-auto rounded-md border border-slate-200">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-left text-slate-500">
                    <tr>
                      <th className="px-3 py-2 font-medium">Invoice</th>
                      <th className="px-3 py-2 font-medium">Client / Matter</th>
                      <th className="px-3 py-2 font-medium text-right">Amount</th>
                      <th className="px-3 py-2 font-medium">Last nudge</th>
                    </tr>
                  </thead>
                  <tbody data-testid="roi-drilldown-tbody">
                    {data.invoices.map((inv) => (
                      <tr key={inv.invoice_id} className="border-t border-slate-100" data-testid={`roi-inv-${inv.invoice_id}`}>
                        <td className="px-3 py-2 font-mono text-[11px] text-slate-700">{inv.invoice_number}</td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-[#1A3A5C]">{inv.client_name}</div>
                          <div className="text-[10px] text-slate-500">{inv.matter_ref || '—'} · {inv.description || ''}</div>
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-[#0F766E]">A${inv.amount_aud.toLocaleString()}</td>
                        <td className="px-3 py-2 text-[10px] text-slate-500">
                          {inv.reminder_milestone_days}d milestone · {new Date(inv.reminder_last_sent_at).toLocaleDateString('en-AU')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function PreviewDialog({ open, onOpenChange, activeTone, activeMilestones }) {
  const [tab, setTab] = React.useState(String(activeMilestones[0] || 7));
  const [cache, setCache] = React.useState({});  // { "7|null|first|amount|desc": {subject, html, tone} }
  const [loading, setLoading] = React.useState(false);
  // Custom preview data — persists across tab switches but resets when dialog re-opens.
  const [first, setFirst] = React.useState('Alex');
  const [amount, setAmount] = React.useState('4200');
  const [desc, setDesc] = React.useState('migration services');

  const key = `${tab}|${activeTone || 'auto'}|${first}|${amount}|${desc}`;

  React.useEffect(() => {
    if (!open) return;
    if (cache[key]) return;
    setLoading(true);
    reminderPrefs.preview({
      milestone_days: Number(tab),
      tone_override: activeTone || null,
      applicant_first: first,
      amount: parseFloat(amount) || 0,
      description: desc,
    })
      .then((r) => setCache((c) => ({ ...c, [key]: r })))
      .catch(() => setCache((c) => ({ ...c, [key]: { html: '<div style="color:#c00">Preview failed. Please save your preferences and try again.</div>' } })))
      .finally(() => setLoading(false));
  }, [open, tab, activeTone, key, cache, first, amount, desc]);

  React.useEffect(() => {
    if (open && !activeMilestones.includes(Number(tab)) && activeMilestones.length > 0) {
      setTab(String(activeMilestones[0]));
    }
  }, [open, activeMilestones, tab]);

  React.useEffect(() => { setCache({}); }, [activeTone]);
  // Debounce cache-clear when inputs change so we don't spam the endpoint on every keystroke.
  React.useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => { /* cache is already keyed by inputs; nothing to clear */ }, 400);
    return () => clearTimeout(t);
  }, [first, amount, desc, open]);

  const current = cache[key];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="reminder-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-[#2ECC9A]" /> Reminder email preview
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-slate-500">
          This is exactly what a client sees at each milestone
          {activeTone ? <> — currently forced to <b>{activeTone}</b>.</> : <> — currently graduating gentle → firm → urgent.</>}
        </div>

        <div className="mt-2 grid grid-cols-3 gap-2 rounded-md border border-slate-200 bg-slate-50 p-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Applicant first name</label>
            <input value={first} onChange={(e) => setFirst(e.target.value)}
                   data-testid="reminder-preview-first"
                   className="mt-1 h-8 w-full rounded border border-slate-200 bg-white px-2 text-xs" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Amount (A$)</label>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                   data-testid="reminder-preview-amount"
                   className="mt-1 h-8 w-full rounded border border-slate-200 bg-white px-2 text-xs" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Description</label>
            <input value={desc} onChange={(e) => setDesc(e.target.value)}
                   data-testid="reminder-preview-desc"
                   className="mt-1 h-8 w-full rounded border border-slate-200 bg-white px-2 text-xs" />
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList>
            {[7, 14, 21].map((d) => {
              const enabled = activeMilestones.includes(d);
              return (
                <TabsTrigger key={d} value={String(d)} disabled={!enabled}
                             data-testid={`reminder-preview-tab-${d}`}>
                  {d} days {enabled ? '' : '(off)'}
                </TabsTrigger>
              );
            })}
          </TabsList>
          {[7, 14, 21].map((d) => (
            <TabsContent key={d} value={String(d)}>
              {loading || !current ? (
                <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
              ) : (
                <div data-testid={`reminder-preview-body-${d}`}>
                  <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-2 text-[11px] text-slate-600">
                    <div><span className="font-semibold">Subject:</span> {current.subject}</div>
                    <div>
                      <span className="font-semibold">Tone:</span> <span className="capitalize">{current.tone}</span>
                      {current.used_custom_template && (
                        <span data-testid="reminder-preview-custom-badge"
                              className="ml-2 rounded-full bg-[#2ECC9A]/15 px-2 py-0.5 text-[9px] font-semibold text-[#0F766E]">
                          CUSTOM
                        </span>
                      )}
                    </div>
                  </div>
                  <iframe
                    title={`preview-${d}d`}
                    sandbox=""
                    srcDoc={current.html || ''}
                    className="mt-2 h-[420px] w-full rounded-md border border-slate-200"
                  />
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
